import { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";

import { LinkTextButton } from "@vega-core/components/Buttons/LinkTextButton";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { CoreTable } from "@vega-core/components/Table/CoreTable/CoreTable";
import { m } from "@vega-core/paraglide/messages";

import { useCoInvestDealReadOnly } from "../../hooks/useCoInvestDealReadOnly";
import { type FlatNdaRow, useLpNdaTable } from "../../hooks/useLpNdaTable";

type LpNdaTableProps = {
  dealId: string;
};

export const LpNdaTable = ({ dealId }: LpNdaTableProps) => {
  const {
    ndaAssociations,
    lpNameMapping,
    isLoading,
    handleRemoveNda,
    handleViewNda,
    isDeleting,
  } = useLpNdaTable({ dealId });

  const { isMutationLocked } = useCoInvestDealReadOnly();
  // Closed deals lock NDA removal (view stays)
  const canRemoveNda = !isMutationLocked;

  const columns: ColumnDef<FlatNdaRow>[] = useMemo(
    () => [
      {
        accessorKey: "limitedPartnerId",
        header: m.coInvestments_tracking_limitedPartner(),
        cell: ({ row }) => (
          <div className="text-text/primary">
            {lpNameMapping?.[row.original.limitedPartnerId ?? ""] ||
              row.original.limitedPartnerId}
          </div>
        ),
      },
      {
        accessorKey: "document",
        header: m.coInvestments_ndaDocument(),
        cell: ({ row }) => {
          const documentName = row.original.document.nameOfDocument;

          return (
            <LinkTextButton
              ariaLabel={m.coInvestments_ndaDocument_viewAriaLabel({
                documentName: row.original.document.nameOfDocument,
              })}
              className="rich-link"
              onClick={() => handleViewNda(row.original)}
              text={documentName}
            />
          );
        },
      },
      ...(canRemoveNda
        ? [
            {
              id: "actions",
              header: "",
              cell: ({ row }) => (
                <ThemedButton
                  dataTestId={`remove-nda-${row.original.id}`}
                  disabled={isDeleting}
                  onClick={() => handleRemoveNda(row.original)}
                  variant="destructive"
                >
                  {isDeleting ? m.common_pending() : m.common_remove()}
                </ThemedButton>
              ),
            } satisfies ColumnDef<FlatNdaRow>,
          ]
        : []),
    ],
    [handleViewNda, handleRemoveNda, isDeleting, lpNameMapping, canRemoveNda],
  );

  return (
    <CoreTable
      columns={columns}
      data={ndaAssociations}
      dataTestId="lp-nda-table"
      emptyState={{
        title: m.coInvestments_noNdaDocuments(),
      }}
      isLoading={isLoading}
    />
  );
};
